import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { brand } from '../shared-theme/themePrimitives';
import { GROUP_NAME } from '../App';

// Quote block component
const QuoteBlock: React.FC<{ children: React.ReactNode; attribution: string }> = ({ children, attribution }) => {
  const theme = useTheme();
  const { mode, systemMode } = useColorScheme();
  
  // Get the actual effective theme mode (handles "system" mode correctly)
  const effectiveMode = mode === 'system' ? systemMode : mode;
  
  return (
    <Box
      sx={{
        borderLeft: '4px solid',
        borderColor: 'secondary.main',
        pl: 3,
        py: 2,
        my: 3,
        backgroundColor: effectiveMode === 'dark' 
          ? '#232323'
          : alpha(theme.palette.grey[100], 0.8),
        fontStyle: 'italic',
        borderRadius: 1,
      }}
    >
      {children}
      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 'bold' }}>
        — {attribution}
      </Typography>
    </Box>
  );
};

const Home: React.FC = () => {
  const location = useLocation();
  const { mode, systemMode } = useColorScheme();
  
  // Get the actual effective theme mode (handles "system" mode correctly)
  const effectiveMode = mode === 'system' ? systemMode : mode;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section */}
    
        <Card sx={{ mb: 4, p: 2 }}>
          <CardContent>
            <Typography variant="h3" component="h1" gutterBottom>
              From Movement to Legislation
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              Have you ever wished the social movements you support would translate into legislative demands or concrete actions? This site tries to do exactly that.
            </Typography>
            <Typography variant="body1" paragraph>
                Our current focus: prevent growth of the immigrant exploitation economy, private businesses profiting from violating civil and human rights
            </Typography>
          </CardContent>
        </Card>

      {/* Current Challenge */}
      <motion.div
        initial={{ opacity: 0, x: -100 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Federal Force Expansion
            </Typography>
            <Typography variant="body1" paragraph>
            ICE is over policing and terrorizing our Colorado communities. We can expect this to grow. The ACLU FOIA request shows that{' '}
              <Link 
                href="https://www.cpr.org/2025/07/10/proposed-ice-detention-facilities-colorado-aclu/"
                target="_blank"
                rel="noopener noreferrer"
              >
                ICE is expanding to up to 6 additional detention facilities in Colorado
              </Link>
              </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
                Palantir, headquartered in Denver, 
                <Link 
                    href="https://www.wired.com/story/ice-palantir-immigrationos/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    was awarded a federal contract to develop a new surveillance system for ICE;
                </Link>
                 they were separately awarded 
                 <Link
                 href="https://newrepublic.com/post/195904/trump-palantir-data-americans"
                 target="_blank"
                 rel="noopener noreferrer"
                 >
                 a contract to build massive databases on every resident of the US including the private information of US citizens.
                    </Link>
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }} paragraph>
                Military and federal law enforcement continue to occupy the streets of California, and the Trump administration has made it clear that other blue states are next on the list. <b>We need a plan.</b>
            </Typography>
            <Typography variant="body1" paragraph>
              Many of our state officials will point to our laws and say we have done all we can. The {GROUP_NAME} thinks we are not being creative enough.
            </Typography>
          </CardContent>
        </Card>
      </motion.div>

      {/* Private Facilities Gap */}
      <motion.div
        initial={{ opacity: 0, x: 100 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              The Private Facilities Loophole
            </Typography>
            <QuoteBlock attribution="Colorado Public Radio">
              <Typography variant="body1">
                In 2023, Colorado joined{' '}
                <Link 
                  href="https://www.cpr.org/2023/06/09/colorado-jails-prisons-ice-detainees-agreements-ban/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  a list of states that banned local jails and prisons from partnering
                </Link>
                {' '}with ICE to hold immigration detainees. But that policy only applies to publicly owned facilities, not private ones.
              </Typography>
            </QuoteBlock>
          </CardContent>
        </Card>
      </motion.div>

      {/* Colorado Laws Table */}
      <motion.div
        initial={{ opacity: 0, x: 100 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <Card sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              The Good News, But Is It Enough?
            </Typography>
            <Typography variant="body1" paragraph>
              <Link 
                href="https://www.visaverge.com/knowledge/overview-of-colorados-sanctuary-status-and-protections-for-undocumented-immigrants-in-2025/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Colorado already has some of the most restrictive non-cooperation laws with ICE officers.
              </Link>
            </Typography>
          <CardContent>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Bill</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Link 
                          href="https://leg.colorado.gov/bills/hb19-1124"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <strong>HB19-1124</strong>
                        </Link>
                      </TableCell>
                      <TableCell><strong>Prohibits arrest/detention based on immigration status; limits info sharing with ICE</strong></TableCell>
                      <TableCell>
                        <Chip label="Enacted 2019" color="success" size="small" />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Link 
                          href="https://leg.colorado.gov/bills/sb21-131"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <strong>SB21-131</strong>
                        </Link>
                      </TableCell>
                      <TableCell><strong>Restricts state employee cooperation with ICE</strong></TableCell>
                      <TableCell>
                        <Chip label="Enacted 2021" color="success" size="small" />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Link 
                          href="https://leg.colorado.gov/bills/sb20-083"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <strong>SB20-083</strong>
                        </Link>
                      </TableCell>
                      <TableCell><strong>Prohibits immigration arrests at courthouses</strong></TableCell>
                      <TableCell>
                        <Chip label="Enacted 2020" color="success" size="small" />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Link 
                          href="https://leg.colorado.gov/bills/sb25-276"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <strong>SB25-276</strong>
                        </Link>
                      </TableCell>
                      <TableCell><strong>Strengthens protections: bars holding for ICE post-bond, data privacy, civil penalties</strong></TableCell>
                      <TableCell>
                        <Chip label="Signed May 2025; effective June 30, 2025" color="info" size="small" />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 'bold' }}>
        — Visa Verge
    </Typography>
      <Typography variant="body2" sx={{ mt: 2 }}>
      But we know that the fight is far from over. ICE and its contractors continue to operate in Colorado with total impunity. 
      <Link
      href="https://www.westword.com/news/lawsuit-halts-colorado-governors-ice-order-more-could-come-24908860">
      Many public officials
      </Link> and 
      <Link
      href="https://coloradoimmigrant.org/ice-detains-utah-student-in-mesa-county/">local police departments</Link> are violating state law and assisting ICE when not legally required to do so. <strong>We need to do more</strong>.
      </Typography>
          </CardContent>
        </Card>
      </motion.div>


      {/* Our Solution */}
      <motion.div
        initial={{ opacity: 0, x: -100 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Our Creative Solution
            </Typography>
            <Typography variant="body1" paragraph>
              Take a look at our{' '}
              <Link component={RouterLink} to="/legislation?bill=fact-sheet">
                fact sheet
              </Link>
              . It would be impossible to prohibit the feds from operating within Colorado, and mostly impossible to{' '}
              <Link 
                href="https://www.ilrc.org/press-room/biden-administration-partners-private-prisons-kill-popular-ca-law-end-private-detention"
                target="_blank"
                rel="noopener noreferrer"
              >
                outright prohibit private companies
              </Link>
              {' '}from contracting with the federal government. <strong>However</strong> - we can slow the pace of this expansion and create a chilling effect on the civil rights violation exploitation economy.
            </Typography>
            <Typography variant="body1" paragraph>
              Notably, Texas SB 8 abridged the right to have an abortion before the overturning of Roe V Wade by creating a financial deterrent. The Supreme Court upheld it on a technicality (though there was some legal concern over whether citizens who were not damaged had standing to bring a case). This provides a template for abridging other things that would otherwise not survive a constitutional challenge in criminal court.
            </Typography>
          </CardContent>
        </Card>
      </motion.div>

      {/* Legislative Template */}
      <motion.div
        initial={{ opacity: 0, x: 100 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Our Legislative Template
            </Typography>
            <Typography variant="body1" paragraph>
              Take a look at our{' '}
              <Link component={RouterLink} to="/legislation?bill=accountability-for-civil-rights">
                proposed legislative template
              </Link>
              . The executive summary is this:
            </Typography>
            
            <Box component="ul" sx={{ 
              display: 'inline-block', 
              paddingLeft: 0, 
              textAlign: 'left',
              mb: 2 
            }}>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Prohibit private companies from assisting federal agencies in violating the civil rights of Coloradans
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Violations are a civil offense
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Create a public bulletin naming and shaming these companies and providing constructive knowledge they are potentially liable
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Empower the state and individuals to bring financial penalties against violators
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </motion.div>

      {/* Financial Strategy */}
      <motion.div
        initial={{ opacity: 0, x: -100 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <Card sx={{ 
          mb: 4, 
          backgroundColor: `${effectiveMode === 'dark' ? brand[700] : brand[400]} !important`,
          color: `${brand[50]} !important`,
          border: effectiveMode === 'dark' ? `2px solid ${brand[500]}` : 'none',
          '& .MuiCardContent-root': {
            backgroundColor: 'transparent !important',
          }
        }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ color: 'inherit', fontWeight: 600 }}>
              Use Financial Penalties
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: 'inherit' }}>
              If you want to contract with ICE or other federal agencies, you are liable for its mistakes. We likely cannot prevent a company from contracting with the government for these purposes, but we will try our hardest to prevent them from making any money off of it.
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'inherit' }}>
              If they can't make money off of it, they won't do it.
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ mt: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Remain Aggressive
            </Typography>
            {/* Content coming soon */}
            <Typography variant="body1" paragraph>
                We urge our legislators to <i>remain aggressive</i>. The work is not yet done - use every tool at our state's disposal to resist Project 2025 and Trump's agenda. Take the fight to the courts, and don't comply in advance. Pursue new legal theories and strategies - be creative. Our laws are a good starting point, but we need better enforcement mechanisms and additional protections. We demand Governor Polis call a special session of the state legislature to address these urgent needs.
            </Typography>
          </CardContent>
        </Card>
        
      </motion.div>
    </Container>
  );
};

export default Home;